"use client";

import { c as _c } from "react/compiler-runtime";
import { appendSkippedDropId, MEMES_QUICK_VOTE_REPLENISH_THRESHOLD } from "@/hooks/memesQuickVote.helpers";
import { createInitialMemesQuickVoteDiscoveryState, deferMemesQuickVoteDropId, deriveMemesQuickVoteDiscoverySnapshot, getMemesQuickVoteActiveCandidateId, getMemesQuickVoteDiscoveredQueue, getMemesQuickVoteNextCandidateId, isMemesQuickVoteExhausted, type MemesQuickVoteDiscoveryPage, type MemesQuickVoteDiscoveryState, removeMemesQuickVoteDropId, shouldFetchMemesQuickVotePage } from "@/hooks/memesQuickVote.queue.helpers";
import { fetchMemesQuickVoteDiscoveryBatch, getMemesQuickVoteDiscoveryQueryKey, getMemesQuickVoteDiscoveryStateKey, type MemesQuickVoteDiscoveryQueryData } from "@/hooks/memesQuickVote.query";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
const EMPTY_DISCOVERY_PAGES: readonly MemesQuickVoteDiscoveryPage[] = [];
type KeyedMemesQuickVoteDiscoveryState = MemesQuickVoteDiscoveryState & {
  readonly fetchVersion: number;
  readonly key: string;
};
const createKeyedMemesQuickVoteDiscoveryState = (key: string): KeyedMemesQuickVoteDiscoveryState => ({
  ...createInitialMemesQuickVoteDiscoveryState(),
  fetchVersion: 0,
  key
});
const getCurrentMemesQuickVoteDiscoveryState = ({
  key,
  state
}: {
  readonly key: string;
  readonly state: KeyedMemesQuickVoteDiscoveryState;
}): KeyedMemesQuickVoteDiscoveryState => state.key === key ? state : createKeyedMemesQuickVoteDiscoveryState(key);
const getMemesQuickVoteDiscoveryQueryKeyIdentity = (candidate: unknown): string | null => {
  if (candidate === undefined || candidate === null) {
    return null;
  }
  if (typeof candidate !== "object" || !("identity" in candidate)) {
    return null;
  }
  const identity = candidate.identity;
  return typeof identity === "string" ? identity : null;
};
const shouldReuseDiscoveryPlaceholderData = ({
  identity,
  previousQuery
}: {
  readonly identity: string;
  readonly previousQuery: {
    readonly queryKey: readonly unknown[];
  } | undefined;
}): boolean => {
  const previousQueryIdentity = getMemesQuickVoteDiscoveryQueryKeyIdentity(previousQuery?.queryKey[1]);
  return previousQueryIdentity === identity;
};
const applyMemesQuickVoteDiscoveryStateUpdate = ({
  current,
  discoveryPages,
  discoveryStateKey,
  isDiscoveryEnabled,
  nextSkippedDropIds,
  transformState
}: {
  readonly current: KeyedMemesQuickVoteDiscoveryState;
  readonly discoveryPages: readonly MemesQuickVoteDiscoveryPage[];
  readonly discoveryStateKey: string;
  readonly isDiscoveryEnabled: boolean;
  readonly nextSkippedDropIds: readonly string[];
  readonly transformState: (state: KeyedMemesQuickVoteDiscoveryState) => MemesQuickVoteDiscoveryState;
}): KeyedMemesQuickVoteDiscoveryState => {
  const baseState = getCurrentMemesQuickVoteDiscoveryState({
    key: discoveryStateKey,
    state: current
  });
  const nextState = transformState(baseState);
  if (nextState === baseState) {
    return baseState;
  }
  const nextSnapshot = deriveMemesQuickVoteDiscoverySnapshot({
    enabled: isDiscoveryEnabled,
    pages: discoveryPages,
    skippedDropIds: nextSkippedDropIds,
    state: nextState
  });
  return {
    ...nextState,
    fetchVersion: shouldFetchMemesQuickVotePage({
      replenishThreshold: MEMES_QUICK_VOTE_REPLENISH_THRESHOLD,
      state: nextSnapshot
    }) ? baseState.fetchVersion + 1 : baseState.fetchVersion,
    key: discoveryStateKey
  };
};
export const useMemesQuickVoteDiscovery = t0 => {
  const $ = _c(68);
  const {
    enabled,
    sessionId,
    skippedDropIds
  } = t0;
  const {
    contextProfile,
    isEnabled,
    memesWaveId
  } = useMemesQuickVoteContext();
  const waveId = typeof memesWaveId === "string" && memesWaveId.length > 0 ? memesWaveId : null;
  const isDiscoveryEnabled = enabled && isEnabled;
  let t1;
  if ($[0] !== contextProfile || $[1] !== isDiscoveryEnabled || $[2] !== sessionId || $[3] !== waveId) {
    t1 = getMemesQuickVoteDiscoveryStateKey({
      contextProfile,
      enabled: isDiscoveryEnabled,
      memesWaveId: waveId,
      sessionId
    });
    $[0] = contextProfile;
    $[1] = isDiscoveryEnabled;
    $[2] = sessionId;
    $[3] = waveId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const discoveryStateKey = t1;
  let t2;
  if ($[5] !== discoveryStateKey) {
    t2 = () => createKeyedMemesQuickVoteDiscoveryState(discoveryStateKey);
    $[5] = discoveryStateKey;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const [storedState, setStoredState] = useState(t2);
  let discoveryState;
  let t3;
  if ($[7] !== discoveryStateKey || $[8] !== storedState || $[9] !== waveId) {
    discoveryState = getCurrentMemesQuickVoteDiscoveryState({
      key: discoveryStateKey,
      state: storedState
    });
    t3 = getMemesQuickVoteDiscoveryQueryKey({
      discoveryStateKey,
      fetchVersion: discoveryState.fetchVersion,
      waveId
    });
    $[7] = discoveryStateKey;
    $[8] = storedState;
    $[9] = waveId;
    $[10] = discoveryState;
    $[11] = t3;
  } else {
    discoveryState = $[10];
    t3 = $[11];
  }
  const queryKey = t3;
  let t4;
  if ($[12] !== discoveryState || $[13] !== skippedDropIds || $[14] !== waveId) {
    t4 = async () => await fetchMemesQuickVoteDiscoveryBatch({
      discoveryState,
      skippedDropIds,
      waveId
    });
    $[12] = discoveryState;
    $[13] = skippedDropIds;
    $[14] = waveId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const fetchDiscoveryBatch = t4;
  const t5 = isDiscoveryEnabled && waveId !== null;
  let t6;
  if ($[16] !== discoveryStateKey) {
    t6 = (previousData, previousQuery) => shouldReuseDiscoveryPlaceholderData({
      identity: discoveryStateKey,
      previousQuery
    }) ? previousData : undefined;
    $[16] = discoveryStateKey;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== fetchDiscoveryBatch || $[19] !== queryKey || $[20] !== t5 || $[21] !== t6) {
    t7 = {
      queryKey,
      enabled: t5,
      queryFn: fetchDiscoveryBatch,
      placeholderData: t6,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false
    };
    $[18] = fetchDiscoveryBatch;
    $[19] = queryKey;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const {
    data: discoveryData,
    isError: hasPageFetchError,
    isFetching: isFetchingPage,
    refetch: refetchDiscovery
  } = useQuery(t7);
  const discoveryPages = discoveryData?.pages ?? EMPTY_DISCOVERY_PAGES;
  let discoverySnapshot;
  let nextCandidateId;
  let queue;
  let removeDropId;
  let resyncDiscovery;
  let retryDiscovery;
  let t10;
  let t11;
  let t12;
  let t8;
  let t9;
  if ($[23] !== discoveryPages || $[24] !== discoveryState || $[25] !== discoveryStateKey || $[26] !== hasPageFetchError || $[27] !== isDiscoveryEnabled || $[28] !== refetchDiscovery || $[29] !== skippedDropIds) {
    discoverySnapshot = deriveMemesQuickVoteDiscoverySnapshot({
      enabled: isDiscoveryEnabled,
      pages: discoveryPages,
      skippedDropIds,
      state: discoveryState
    });
    const activeCandidateId = getMemesQuickVoteActiveCandidateId(discoverySnapshot);
    nextCandidateId = getMemesQuickVoteNextCandidateId(discoverySnapshot);
    queue = getMemesQuickVoteDiscoveredQueue(discoverySnapshot);
    let t13;
    if ($[41] !== discoveryPages || $[42] !== discoveryStateKey || $[43] !== isDiscoveryEnabled || $[44] !== skippedDropIds) {
      t13 = dropId => {
        setStoredState(current => applyMemesQuickVoteDiscoveryStateUpdate({
          current,
          discoveryPages,
          discoveryStateKey,
          isDiscoveryEnabled,
          nextSkippedDropIds: skippedDropIds.filter(value => value !== dropId),
          transformState: state => removeMemesQuickVoteDropId({
            dropId,
            state
          })
        }));
      };
      $[41] = discoveryPages;
      $[42] = discoveryStateKey;
      $[43] = isDiscoveryEnabled;
      $[44] = skippedDropIds;
      $[45] = t13;
    } else {
      t13 = $[45];
    }
    removeDropId = t13;
    let t14;
    if ($[46] !== discoveryPages || $[47] !== discoveryStateKey || $[48] !== isDiscoveryEnabled || $[49] !== skippedDropIds) {
      t14 = dropId_0 => {
        setStoredState(current_0 => applyMemesQuickVoteDiscoveryStateUpdate({
          current: current_0,
          discoveryPages,
          discoveryStateKey,
          isDiscoveryEnabled,
          nextSkippedDropIds: appendSkippedDropId(skippedDropIds, dropId_0),
          transformState: state_0 => deferMemesQuickVoteDropId({
            dropId: dropId_0,
            state: state_0
          })
        }));
      };
      $[46] = discoveryPages;
      $[47] = discoveryStateKey;
      $[48] = isDiscoveryEnabled;
      $[49] = skippedDropIds;
      $[50] = t14;
    } else {
      t14 = $[50];
    }
    const hideDropId = t14;
    let t15;
    if ($[51] !== refetchDiscovery) {
      t15 = () => {
        refetchDiscovery();
      };
      $[51] = refetchDiscovery;
      $[52] = t15;
    } else {
      t15 = $[52];
    }
    retryDiscovery = t15;
    let t16;
    if ($[53] !== discoveryStateKey) {
      t16 = () => {
        setStoredState(current_1 => {
          const baseState = getCurrentMemesQuickVoteDiscoveryState({
            key: discoveryStateKey,
            state: current_1
          });
          return {
            ...createInitialMemesQuickVoteDiscoveryState(),
            fetchVersion: baseState.fetchVersion + 1,
            key: discoveryStateKey
          };
        });
      };
      $[53] = discoveryStateKey;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    resyncDiscovery = t16;
    t8 = activeCandidateId;
    t9 = discoverySnapshot.discoveredDropsById;
    t10 = hasPageFetchError;
    t11 = hideDropId;
    t12 = isDiscoveryEnabled ? isMemesQuickVoteExhausted(discoverySnapshot) : false;
    $[23] = discoveryPages;
    $[24] = discoveryState;
    $[25] = discoveryStateKey;
    $[26] = hasPageFetchError;
    $[27] = isDiscoveryEnabled;
    $[28] = refetchDiscovery;
    $[29] = skippedDropIds;
    $[30] = discoverySnapshot;
    $[31] = nextCandidateId;
    $[32] = queue;
    $[33] = removeDropId;
    $[34] = resyncDiscovery;
    $[35] = retryDiscovery;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t8;
    $[40] = t9;
  } else {
    discoverySnapshot = $[30];
    nextCandidateId = $[31];
    queue = $[32];
    removeDropId = $[33];
    resyncDiscovery = $[34];
    retryDiscovery = $[35];
    t10 = $[36];
    t11 = $[37];
    t12 = $[38];
    t8 = $[39];
    t9 = $[40];
  }
  let t13;
  if ($[55] !== discoverySnapshot.serverCount || $[56] !== isFetchingPage || $[57] !== nextCandidateId || $[58] !== queue || $[59] !== removeDropId || $[60] !== resyncDiscovery || $[61] !== retryDiscovery || $[62] !== t10 || $[63] !== t11 || $[64] !== t12 || $[65] !== t8 || $[66] !== t9) {
    t13 = {
      activeCandidateId: t8,
      discoveredDropsById: t9,
      hasPageFetchError: t10,
      hideDropId: t11,
      isExhausted: t12,
      isFetchingPage,
      nextCandidateId,
      queue,
      removeDropId,
      resyncDiscovery,
      retryDiscovery,
      serverCount: discoverySnapshot.serverCount
    };
    $[55] = discoverySnapshot.serverCount;
    $[56] = isFetchingPage;
    $[57] = nextCandidateId;
    $[58] = queue;
    $[59] = removeDropId;
    $[60] = resyncDiscovery;
    $[61] = retryDiscovery;
    $[62] = t10;
    $[63] = t11;
    $[64] = t12;
    $[65] = t8;
    $[66] = t9;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  return t13;
};